// src/pages/HomePage.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import KakaoChatButton from "../components/KakaoChatButton";
import NaverMap from "../components/NaverMap";
import Popup from "../components/Popup";
import homeContent from "../data/homePageContent.json";
import useDragScroll from "../hooks/useDragScroll";
import { cachedFetch } from "../utils/cachedFetch";
import BlogCommentExpand from "./blog/BlogCommentExpand";

/* ============================================================
 * Hooks
 * ============================================================ */

function useIsMobile(breakpoint = 768) {
  const getMatch = () =>
    typeof window !== "undefined" &&
    window.matchMedia(`(max-width:${breakpoint - 1}px)`).matches;
  const [isMobile, setIsMobile] = useState(getMatch());
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width:${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
}

function useVisible(ref) {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => node && observer.unobserve(node);
  }, [ref]);
  return isVisible;
}

function useFadeSlider(images, trigger, intervalMs = 4500) {
  const [idx, setIdx] = useState(0);
  const imagesKey = images ? images.join("|") : "";
  useEffect(() => { setIdx(0); }, [imagesKey]);
  useEffect(() => {
    if (!trigger || !images?.length) return;
    const t = setInterval(() => {
      setIdx((p) => (p + 1) % images.length);
    }, intervalMs);
    return () => clearInterval(t);
  }, [trigger, images, intervalMs]);
  return idx;
}

/* ============================================================
 * Atoms
 * ============================================================ */

function Eyebrow({ children, dark = false }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`h-px w-8 ${dark ? "bg-amber-300/60" : "bg-amber-700/40"}`} />
      <span
        className={`text-[11px] md:text-xs font-bold tracking-[0.22em] uppercase ${
          dark ? "text-amber-200" : "text-amber-700"
        }`}
      >
        {children}
      </span>
    </div>
  );
}

function SectionTitle({ title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 className="font-nanum text-stone-900 text-2xl md:text-4xl font-bold tracking-tight leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-2 md:mt-3 text-sm md:text-base text-stone-600 leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
      {action && <div className="shrink-0 pb-1">{action}</div>}
    </div>
  );
}

/* ============================================================
 * 체험 카드 (auto-rotating bg image)
 * ============================================================ */

function ExperienceCard({ images, title, subtitle, label, link }) {
  const ref = useRef(null);
  const visible = useVisible(ref);
  const idx = useFadeSlider(images, visible, 4500);
  const url = images?.[idx] || "";

  return (
    <Link to={link} ref={ref} className="group block snap-start">
      <div className="relative aspect-[3/4] md:aspect-[4/5] overflow-hidden rounded-2xl bg-stone-200">
        {/* fade-in 새 이미지 */}
        <div
          key={url}
          className="absolute inset-0 bg-cover bg-center animate-[fadeIn_900ms_ease]"
          style={{ backgroundImage: `url(${url})`, willChange: "opacity" }}
        />
        {/* hover 살짝 줌 */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:scale-[1.04]"
          style={{ backgroundImage: `url(${url})` }}
        />
        {/* 그라데이션 오버레이 */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/75" />

        {/* 텍스트 */}
        <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
          <div className="text-amber-200/90 text-[10px] md:text-[11px] tracking-[0.25em] font-bold uppercase">
            {label}
          </div>
          <div className="mt-1.5 text-white font-nanum font-bold text-base md:text-xl leading-tight">
            {title}
          </div>
          {subtitle && (
            <div className="mt-2 text-white/80 text-[11px] md:text-sm leading-snug line-clamp-2">
              {subtitle}
            </div>
          )}
        </div>

        {/* 우상단 화살표 */}
        <div className="absolute top-3 right-3 size-8 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white text-sm group-hover:bg-white group-hover:text-stone-900 transition">
          →
        </div>
      </div>
    </Link>
  );
}

/* ============================================================
 * 상품 카드 (정사각 이미지, 가격 강조)
 * ============================================================ */

function ProductCard({ p, onClick }) {
  return (
    <button type="button" onClick={onClick} className="group text-left">
      <div className="relative aspect-square overflow-hidden rounded-xl bg-stone-100">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-[1.06]"
          style={{ backgroundImage: `url(${p.image})` }}
        />
      </div>
      <div className="mt-2.5 px-0.5">
        <div className="text-[13px] md:text-sm font-bold text-stone-900 truncate">
          {p.name}
        </div>
        <div className="mt-0.5 text-[11px] md:text-xs text-stone-500 line-clamp-1">
          {p.desc}
        </div>
        <div className="mt-1.5 text-[14px] md:text-base font-extrabold text-stone-900">
          {p.priceText}
        </div>
      </div>
    </button>
  );
}

/* ============================================================
 * 반딧불 소식 (홈 섹션)
 * ============================================================ */

const NEWS_CATEGORIES = [
  { key: "cafe", label: "카페 소식", emoji: "☕", basePath: "/cafe/scenes" },
  { key: "kerzen", label: "캔들 체험 스토리", emoji: "🕯️", basePath: "/kerzenziehen/scenes" },
  { key: "freserved", label: "프리저브드 체험 스토리", emoji: "🌸", basePath: "/freserved/scenes" },
];

const NEWS_PAGE_SIZE = 3;   // 한 번에 가져올 카드 개수

function BlogNewsSection() {
  // 각 카테고리별 표시 가능한 항목이 하나라도 있는지 체크용 (헤더 표시)
  const [anyLoaded, setAnyLoaded] = useState(false);

  // 첫 페이지를 모든 카테고리에서 동시에 조회 — 빈 카테고리 헤더 숨김용 (캐시)
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      NEWS_CATEGORIES.map((c) =>
        cachedFetch(`/api/blog?category=${c.key}&limit=1&offset=0`, { ttl: 60_000 })
          .catch(() => ({ items: [], total: 0 }))
      )
    ).then((results) => {
      if (cancelled) return;
      const any = results.some(
        (r) => (r?.total ?? r?.items?.length ?? 0) > 0
      );
      setAnyLoaded(any);
    });
    return () => { cancelled = true; };
  }, []);

  if (!anyLoaded) return null;

  return (
    <section className="px-5 md:px-8 pt-14 md:pt-24">
      <div className="max-w-[1200px] mx-auto">
        <Eyebrow>News</Eyebrow>
        <div className="mt-3 mb-6 md:mb-10">
          <SectionTitle
            title="반딧불 소식"
            subtitle="카페·캔들·프리저브드의 최근 이야기"
          />
        </div>

        <div className="space-y-8 md:space-y-10">
          {NEWS_CATEGORIES.map((c) => (
            <CategoryLane key={c.key} category={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

// 카테고리 한 줄 — 점진적 lazy load 담당
function CategoryLane({ category }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);  // 최초 로드 완료 여부

  const loadingRef = useRef(false);
  const hasMore = items.length < total;

  const loadMore = useCallback(async () => {
    if (loadingRef.current) return;
    if (loaded && !hasMore) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const offset = items.length;
      // 첫 페이지(offset=0)는 캐시 우선 (즉시 표시) / 이후 페이지는 직접 fetch
      const data = offset === 0
        ? await cachedFetch(
            `/api/blog?category=${category.key}&limit=${NEWS_PAGE_SIZE}&offset=0`,
            { ttl: 60_000 }
          )
        : await (await fetch(
            `/api/blog?category=${category.key}&limit=${NEWS_PAGE_SIZE}&offset=${offset}`
          )).json();
      const newItems = Array.isArray(data?.items) ? data.items : [];
      const newTotal = Number(data?.total ?? newItems.length);
      setItems((prev) => [...prev, ...newItems]);
      setTotal(newTotal);
    } catch {
      // 실패 시 조용히 무시
    } finally {
      setLoaded(true);
      setLoading(false);
      loadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.key, items.length, hasMore, loaded]);

  // 최초 1회 로드
  useEffect(() => {
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loaded && items.length === 0) return null;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-nanum text-base md:text-xl font-bold text-stone-900 flex items-center gap-2">
          <span>{category.emoji}</span>
          <span>{category.label}</span>
        </h3>
        <Link
          to={category.basePath}
          className="text-[12px] md:text-sm font-bold text-amber-700 hover:text-amber-900 hover:underline whitespace-nowrap"
        >
          더 보기 →
        </Link>
      </div>
      <NewsCardRow
        items={items}
        basePath={category.basePath}
        onNearEnd={loadMore}
        hasMore={hasMore}
        loading={loading}
      />
    </div>
  );
}

function NewsCardRow({ items, basePath, onNearEnd, hasMore, loading }) {
  const ref = useRef(null);
  useDragScroll(ref);   // ✅ PC 마우스 드래그 스크롤

  // 스크롤이 끝에 가까워지면 추가 로드
  useEffect(() => {
    const el = ref.current;
    if (!el || !onNearEnd) return;
    const onScroll = () => {
      const remain = el.scrollWidth - (el.scrollLeft + el.clientWidth);
      if (remain < 200 && hasMore && !loading) {
        onNearEnd();
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [onNearEnd, hasMore, loading]);

  // ✅ 초기 로드 후 가로 overflow 가 없으면(= 드래그 불가) 즉시 추가 로드
  //    (PC에서 3장이 컨테이너에 딱 맞아 스크롤 못 하는 케이스 방지)
  useEffect(() => {
    const el = ref.current;
    if (!el || !onNearEnd) return;
    if (!hasMore || loading) return;
    // 다음 페인트 후 측정
    const id = requestAnimationFrame(() => {
      const overflow = el.scrollWidth - el.clientWidth;
      if (overflow < 50) onNearEnd();
    });
    return () => cancelAnimationFrame(id);
  }, [items.length, hasMore, loading, onNearEnd]);

  return (
    <div
      ref={ref}
      className="
        flex
        gap-3 md:gap-5
        overflow-x-auto overflow-y-visible
        snap-x snap-mandatory
        -mx-5 md:-mx-1 px-5 md:px-1
        pb-2
        [scrollbar-width:none] [-ms-overflow-style:none]
        [&::-webkit-scrollbar]:hidden
      "
      style={{
        // touch-action 은 기본값(auto) 유지 → 세로 스와이프 시 페이지 스크롤 가능
        WebkitOverflowScrolling: "touch",
        cursor: "grab",
      }}
    >
      {items.map((p) => (
        <div
          key={p.id}
          className="shrink-0 w-[44%] sm:w-[32%] md:w-[28%] snap-start"
        >
          <NewsCard post={p} basePath={basePath} />
        </div>
      ))}
      {loading && (
        <div className="shrink-0 w-[44%] sm:w-[32%] md:w-[calc(33.333%-0.83rem)] flex items-center justify-center text-stone-400 text-sm">
          불러오는 중...
        </div>
      )}
    </div>
  );
}

function NewsCard({ post, basePath }) {
  return (
    <div className="group rounded-2xl border border-black/5 bg-white overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] transition">
      <Link to={`${basePath}/${post.id}`} className="block">
        <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
          {post.thumbnail ? (
            <img
              src={post.thumbnail}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-3xl md:text-5xl text-slate-300">
              📝
            </div>
          )}
        </div>
        <div className="p-2.5 md:p-4">
          <div className="text-[10px] md:text-xs text-slate-500">
            {post.created_at ? post.created_at.slice(0, 10) : ""}
          </div>
          {/* ✅ 항상 2줄 높이 확보 → 카드 크기 통일 */}
          <div
            className="mt-1 text-[12px] md:text-base font-bold text-slate-900 line-clamp-2 leading-snug"
            style={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              minHeight: "2.6em",   // 글줄 높이 × 2 정도 — 1줄이어도 2줄 공간 유지
            }}
          >
            {post.title}
          </div>
        </div>
      </Link>
      <BlogCommentExpand
        blogId={post.id}
        initialCount={post.comment_count || 0}
        theme="light"
      />
    </div>
  );
}

/* ============================================================
 * 이번 주 게임 랭킹 (홈 섹션 - 단일 카드)
 * ============================================================ */

const GAME_RANK_META = [
  { id: "number-click", title: "숫자 찾기",   emoji: "🔢", path: "/game/number-click",
    formatScore: (ms) => `${(ms / 1000).toFixed(2)}초`, higherIsBetter: false },
  { id: "korean-typing", title: "타자게임",   emoji: "⌨️", path: "/game/korean-typing",
    formatScore: (kpm) => `${kpm} 타/분`, higherIsBetter: true },
  { id: "dodge",         title: "똥 피하기", emoji: "💩", path: "/game/dodge",
    formatScore: (ms) => `${(ms / 1000).toFixed(1)}초`, higherIsBetter: true },
  { id: "math-quiz",     title: "덧셈 퀴즈", emoji: "🧮", path: "/game/math-quiz",
    formatScore: (n) => `${n}문제`, higherIsBetter: true },
];

function WeeklyGameRankSection() {
  const [rankings, setRankings] = useState({});

  useEffect(() => {
    let cancelled = false;
    cachedFetch("/api/game/rankings", { ttl: 30_000 })
      .then((data) => !cancelled && setRankings(data || {}))
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="px-5 md:px-8 pt-14 md:pt-24">
      <div className="max-w-[1200px] mx-auto">
        <Eyebrow>Ranking</Eyebrow>
        <div className="mt-3 mb-6 md:mb-10">
          <SectionTitle
            title="이번 주 게임 랭킹"
            subtitle="매주 월요일 오전 1시에 초기화됩니다."
            action={
              <Link
                to="/game"
                className="text-[12px] md:text-sm font-bold text-amber-700 hover:text-amber-900 hover:underline whitespace-nowrap"
              >
                게임 전체 보기 →
              </Link>
            }
          />
        </div>

        {/* 단일 카드 안 2x2 그리드 */}
        <div className="rounded-3xl border border-black/5 bg-gradient-to-br from-[#1f1f28] to-[#0e0e16] shadow-[0_18px_60px_rgba(0,0,0,0.25)] overflow-hidden">
          <div className="grid grid-cols-2 divide-x divide-y divide-white/10">
            {GAME_RANK_META.map((g) => {
              const list = Array.isArray(rankings[g.id]) ? rankings[g.id] : [];
              const top = list[0];
              return (
                <Link
                  key={g.id}
                  to={g.path}
                  className="group p-4 md:p-6 hover:bg-white/5 transition flex flex-col gap-2"
                >
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="text-xl md:text-2xl">{g.emoji}</div>
                    <div className="font-bold text-white text-sm md:text-lg">{g.title}</div>
                  </div>
                  {top ? (
                    <div className="flex items-baseline justify-between gap-2 mt-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-xs md:text-sm text-amber-300">🥇</span>
                        <span className="text-white text-sm md:text-base font-semibold truncate">
                          {top.player_id}
                        </span>
                      </div>
                      <div className="text-amber-200 text-sm md:text-base font-extrabold whitespace-nowrap">
                        {g.formatScore(top.score)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-white/40 text-xs md:text-sm mt-1">
                      아직 기록이 없습니다
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 * 메인
 * ============================================================ */

function HomePage() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // 팝업
  const [showPopup, setShowPopup] = useState(true);
  useEffect(() => {
    const neverShow = localStorage.getItem(homeContent.popupNeverShowKey);
    if (neverShow === "true") setShowPopup(false);
  }, []);
  const shouldShowPopup = homeContent.eventpopup && showPopup;

  const hero = homeContent.hero;
  const contact = homeContent.contact;
  const map = homeContent.map;
  const cafeSection = homeContent.sections.cafe;
  const candleSection = homeContent.sections.candleexperience;
  const preservedSection = homeContent.sections.preservedexperience;
  const shopPopular = homeContent.shopPopular ?? homeContent.shoppopular;
  const products = useMemo(
    () => (Array.isArray(shopPopular?.products) ? shopPopular.products : []),
    [shopPopular]
  );

  const cafeImages = useMemo(
    () => (isMobile ? cafeSection.images.mobile : cafeSection.images.pc),
    [isMobile, cafeSection.images]
  );
  const candleImages = useMemo(
    () => (isMobile ? candleSection.images.mobile : candleSection.images.pc),
    [isMobile, candleSection.images]
  );
  const preservedImages = useMemo(
    () => (isMobile ? preservedSection.images.mobile : preservedSection.images.pc),
    [isMobile, preservedSection.images]
  );

  const goProduct = useCallback(
    (id) => navigate(`/mall/product/${encodeURIComponent(id)}`),
    [navigate]
  );

  return (
    <div className="bg-[#fafaf7] text-stone-900">
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes heroZoom { from { transform: scale(1) } to { transform: scale(1.08) } }
      `}</style>

      {shouldShowPopup && (
        <Popup
          onClose={() => setShowPopup(false)}
          onNeverShow={() => {
            localStorage.setItem(homeContent.popupNeverShowKey, "true");
            setShowPopup(false);
          }}
        />
      )}

      {/* ============== HERO (시네마틱 풀블리드) ============== */}
      <section className="relative w-full overflow-hidden">
        <div className="relative h-[78vh] min-h-[560px] md:h-[88vh] md:min-h-[640px]">
          <div
            className="absolute inset-0 bg-cover bg-center will-change-transform"
            style={{
              backgroundImage: `url('${hero.bgImage}')`,
              animation: "heroZoom 18s ease-out infinite alternate",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/65" />

          <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
            <div className="text-amber-200/95 text-[10px] md:text-xs font-semibold tracking-[0.32em] uppercase mb-5">
              Firefly Cafe
            </div>
            <h1 className="font-nanum text-white text-[44px] sm:text-6xl md:text-7xl font-bold tracking-tight leading-[1.1] drop-shadow-[0_4px_20px_rgba(0,0,0,0.45)]">
              {hero.title}
            </h1>
            <div className="mt-6 mx-auto h-px w-12 bg-amber-300/70" />
            <p className="mt-6 max-w-md md:max-w-xl text-white/90 text-sm md:text-base leading-relaxed">
              {hero.descriptionLines?.[0] ?? ""}
              <br className="hidden md:block" />
              {hero.descriptionLines?.[1] ?? ""}
            </p>
          </div>

          {/* scroll cue */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-[10px] tracking-[0.4em] uppercase">
            <div className="flex flex-col items-center gap-1">
              <span>Scroll</span>
              <span className="h-6 w-px bg-white/50 animate-pulse" />
            </div>
          </div>
        </div>
      </section>

      {/* ============== ABOUT (1F · 2F) ============== */}
      <section className="px-5 md:px-8 pt-12 md:pt-20 pb-2 md:pb-4">
        <div className="grid grid-cols-2 gap-3 md:gap-6 max-w-[920px] mx-auto">
          <div className="rounded-2xl bg-amber-50/80 border border-amber-100 p-4 md:p-6">
            <div className="text-[10px] md:text-xs tracking-[0.22em] font-extrabold text-amber-700 uppercase">
              1F
            </div>
            <div className="mt-2 font-nanum text-base md:text-2xl font-bold text-stone-900">
              감성 카페
            </div>
            <div className="mt-1 text-[11px] md:text-sm text-stone-600 leading-snug">
              아늑한 공간, 향긋한 한 잔
            </div>
          </div>
          <div className="rounded-2xl bg-rose-50/80 border border-rose-100 p-4 md:p-6">
            <div className="text-[10px] md:text-xs tracking-[0.22em] font-extrabold text-rose-700 uppercase">
              2F
            </div>
            <div className="mt-2 font-nanum text-base md:text-2xl font-bold text-stone-900">
              체험 · 갤러리
            </div>
            <div className="mt-1 text-[11px] md:text-sm text-stone-600 leading-snug">
              캔들과 꽃의 특별한 시간
            </div>
          </div>
        </div>
      </section>

      {/* ============== EXPERIENCE (3 카드: 모바일 가로 스크롤 / PC 3열) ============== */}
      <section className="px-5 md:px-8 pt-12 md:pt-20">
        <div className="max-w-[1200px] mx-auto">
          <Eyebrow>Experience</Eyebrow>
          <div className="mt-3 mb-6 md:mb-10">
            <SectionTitle
              title="세 가지 시간, 한 공간"
              subtitle="반딧불에서 천천히 누리는 작은 사치"
            />
          </div>

          {/* 모바일: 가로 스크롤(peek) / PC: 3열 그리드 */}
          <div
            className="
              flex md:grid md:grid-cols-3
              gap-3 md:gap-5
              overflow-x-auto md:overflow-visible
              snap-x snap-mandatory
              -mx-5 md:mx-0 px-5 md:px-0
              pb-2 md:pb-0
              [scrollbar-width:none] [-ms-overflow-style:none]
              [&::-webkit-scrollbar]:hidden
            "
          >
            <div className="shrink-0 w-[72%] md:w-auto">
              <ExperienceCard
                images={cafeImages}
                title="감성 카페"
                subtitle={cafeSection.messages?.[0]}
                label="Cafe"
                link="/cafe"
              />
            </div>
            <div className="shrink-0 w-[72%] md:w-auto">
              <ExperienceCard
                images={candleImages}
                title="유럽 전통 캔들 만들기"
                subtitle={candleSection.messages?.[0]}
                label="Candle"
                link="/kerzenziehen"
              />
            </div>
            <div className="shrink-0 w-[72%] md:w-auto">
              <ExperienceCard
                images={preservedImages}
                title="프리저브드 플라워"
                subtitle={preservedSection.messages?.[0]}
                label="Flower"
                link="/freserved"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============== NEWS (반딧불 소식 - 카페/캔들/프리저브드 최신 3개씩) ============== */}
      <BlogNewsSection />

      {/* ============== POPULAR (2x2 그리드) ============== */}
      {products.length > 0 && (
        <section className="px-5 md:px-8 pt-14 md:pt-24">
          <div className="max-w-[1200px] mx-auto">
            <Eyebrow>Popular</Eyebrow>
            <div className="mt-3 mb-6 md:mb-10">
              <SectionTitle
                title="인기 상품"
                subtitle={shopPopular?.desc || ""}
                action={
                  <Link
                    to="/mall"
                    className="text-[12px] md:text-sm font-bold text-amber-700 hover:text-amber-900 underline-offset-4 hover:underline whitespace-nowrap"
                  >
                    전체 보러가기 →
                  </Link>
                }
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              {products.map((p) => (
                <ProductCard key={p.id} p={p} onClick={() => goProduct(p.id)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============== GAME RANKING (이번 주 랭킹) ============== */}
      <WeeklyGameRankSection />

      {/* ============== VISIT (지도) ============== */}
      <section className="px-5 md:px-8 pt-14 md:pt-24">
        <div className="max-w-[1200px] mx-auto">
          <Eyebrow>Visit Us</Eyebrow>
          <div className="mt-3 mb-6 md:mb-10">
            <SectionTitle
              title="오시는 길"
              subtitle={map.desc}
            />
          </div>

          <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
            <div className="h-[280px] md:h-[420px]">
              <NaverMap />
            </div>
          </div>
        </div>
      </section>

      {/* ============== CONTACT (3 pills) ============== */}
      <section
        className="px-5 md:px-8 pt-14 md:pt-24"
        style={{ paddingBottom: "calc(var(--bottom-nav-h) + 32px)" }}
      >
        <div className="max-w-[1200px] mx-auto">
          <Eyebrow>Contact</Eyebrow>
          <div className="mt-3 mb-6 md:mb-10">
            <SectionTitle title={contact.title} subtitle={contact.desc} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <a
              href={contact.kakaoChannel.href}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-2xl border border-yellow-300 bg-yellow-100/80 hover:bg-yellow-200/80 px-5 py-5 transition flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.22em] font-bold text-yellow-800 uppercase">
                  Kakao
                </div>
                <div className="mt-1 font-bold text-stone-900 truncate">
                  {contact.kakaoChannel.text}
                </div>
              </div>
              <span className="text-yellow-700 text-lg shrink-0">→</span>
            </a>

            <a
              href={contact.email.href}
              className="rounded-2xl border border-stone-200 bg-white hover:bg-stone-50 px-5 py-5 transition flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.22em] font-bold text-stone-500 uppercase">
                  Email
                </div>
                <div className="mt-1 font-bold text-stone-900 truncate">
                  firefly4161@gmail.com
                </div>
              </div>
              <span className="text-stone-400 text-lg shrink-0">→</span>
            </a>

            {(contact.downloads || []).map((dl) => (
              <a
                key={dl.href}
                href={dl.href}
                download
                aria-label={dl.ariaLabel}
                className="rounded-2xl border border-stone-900 bg-stone-900 hover:bg-stone-800 px-5 py-5 transition flex items-center justify-between gap-3 text-white"
              >
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.22em] font-bold text-white/70 uppercase">
                    {dl.label || "Brochure"}
                  </div>
                  <div className="mt-1 font-bold truncate">{dl.text}</div>
                </div>
                <span className="text-white/80 text-lg shrink-0">↓</span>
              </a>
            ))}
          </div>

          <div className="mt-6 md:mt-8 flex justify-center">
            <KakaoChatButton />
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
